"use client";

import { cn } from "@gettactic/helpers";
import { mergeRefs } from "@gettactic/helpers/src/dom/mergeRef";
import { ChevronDown } from "lucide-react";
import React from "react";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput
} from "react-international-phone";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../primitives/Popover/Popover";
import { FormControlWrapper } from "../FormControlWrapper";
import {
  disabledCssClasses,
  disabledOutlineClasses
} from "../FormControlWrapper/defines";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "../FormControlWrapper/FormControlWrapperProvider";
import type { PhoneInputProps } from "./PhoneInput.types";
import { stripNationalPrefix } from "./stripNationalPrefix";

interface PhoneNumberInputProps {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  className?: string;
}

// Rendered inside the FormControlWrapper so it gets the wrapper's id (which the label points at)
// and its ARIA state
const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  PhoneNumberInputProps
>(
  (
    { value, onChange, placeholder, isDisabled, isReadOnly, className },
    ref
  ) => {
    const { id } = useFormControlWrapper();
    const a11yProps = useFormControlInputA11y();
    return (
      <input
        {...a11yProps}
        ref={ref}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type="tel"
        autoComplete="tel"
        disabled={isDisabled}
        readOnly={isReadOnly}
        tabIndex={isReadOnly ? -1 : undefined}
        className={cn(
          "flex h-8 w-full border-0 bg-transparent text-sm shadow-none outline-none ring-0 transition-colors placeholder:text-muted-foreground",
          disabledCssClasses,
          disabledOutlineClasses,
          className
        )}
        style={{
          borderWidth: 0,
          outline: 0,
          border: 0,
          boxShadow: "none"
        }}
      />
    );
  }
);
PhoneNumberInput.displayName = "PhoneNumberInput";

/**
 * A phone number field with a country picker (flag and dial code) that formats as you type.
 * Inside react-hook-form use `HookFormPhoneInput`, which also validates the number.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      defaultCountry = "us",
      placeholder,
      inputClassName,
      // FormControlWrapper props
      label,
      error,
      isRequired,
      isOptional,
      isDisabled,
      isReadOnly,
      isInvalid,
      isLoading,
      helperText,
      description,
      info,
      success,
      className,
      wrapperClassName,
      variant,
      leftIcon,
      rightIcon,
      hideEmptyMessages,
      id
    } = props;

    const [isOpen, setIsOpen] = React.useState(false);

    // The dial code lives only in the country picker: prefilling it in the
    // field left a bare "+44" behind that failed validation once submitted,
    // even when the visitor skipped an optional phone
    const phoneInput = usePhoneInput({
      defaultCountry,
      value: value || "",
      disableDialCodeAndPrefix: true,
      onChange: (data) => {
        const hasNationalNumber = /\d/.test(data.inputValue);
        onChange?.(hasNationalNumber ? data.phone : "");
      }
    });

    const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target.value = stripNationalPrefix(
        event.target.value,
        phoneInput.country.iso2
      );
      phoneInput.handlePhoneValueChange(event);
    };

    const currentCountry = parseCountry(
      defaultCountries.find(
        (c) => parseCountry(c).iso2 === phoneInput.country.iso2
      ) || defaultCountries[0]
    );

    return (
      <FormControlWrapper
        enableSlotsInputFocus
        label={label}
        error={error}
        isRequired={isRequired}
        isOptional={isOptional}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
        isLoading={isLoading}
        helperText={helperText}
        description={description}
        info={info}
        success={success}
        className={className}
        wrapperClassName={wrapperClassName}
        variant={variant}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        hideEmptyMessages={hideEmptyMessages}
        id={id}
      >
        <div className="flex w-full items-center">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Country: ${currentCountry.name} (+${currentCountry.dialCode})`}
                disabled={isDisabled || isReadOnly}
                className={cn(
                  "flex h-8 shrink-0 items-center gap-1 rounded-l-sm border-0 bg-transparent px-2 text-sm hover:bg-muted/50",
                  disabledCssClasses
                )}
              >
                <FlagImage iso2={phoneInput.country.iso2} size="20px" />
                <span className="text-content text-xs">
                  +{currentCountry.dialCode}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={4}
              showArrow={false}
              className="max-h-64 w-72 overflow-y-auto p-1"
            >
              {defaultCountries.map((countryData) => {
                const country = parseCountry(countryData);
                return (
                  <button
                    key={country.iso2}
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted/50",
                      phoneInput.country.iso2 === country.iso2 && "bg-muted"
                    )}
                    onClick={() => {
                      phoneInput.setCountry(country.iso2);
                      setIsOpen(false);
                    }}
                  >
                    <FlagImage iso2={country.iso2} size="20px" />
                    <span className="flex-1 truncate">{country.name}</span>
                    <span className="text-content text-xs">
                      +{country.dialCode}
                    </span>
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
          <PhoneNumberInput
            ref={mergeRefs([phoneInput.inputRef, ref])}
            value={phoneInput.inputValue}
            onChange={handleNumberChange}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            className={inputClassName}
          />
        </div>
        <FormControlOutline />
      </FormControlWrapper>
    );
  }
);

PhoneInput.displayName = "PhoneInput";
